import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import { NAV_LINKS, BRAND } from "../data/content";
import GoldButton from "./GoldButton";
import MobileNav from "./MobileNav";

export default function Nav() {
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        // Close mobile nav whenever the route changes.
        setMobileOpen(false);
    }, [location.pathname]);

    // When we are on the home page and not yet scrolled, the nav floats above the
    // cinematic hero (transparent). Elsewhere, or after scroll, a blurred dark bar.
    const isHome = location.pathname === "/";
    const transparent = isHome && !scrolled;

    const barClass = transparent
        ? "bg-transparent border-transparent"
        : "bg-[#0B0B0C]/80 backdrop-blur-md border-[rgba(201,168,106,0.12)]";

    return (
        <>
            <header
                className={`fixed top-0 left-0 right-0 z-50 border-b transition-[background-color,border-color] duration-500 ease-out ${barClass}`}
                data-testid="site-nav"
            >
                {/* The navbar uses a wider container than the global `.shell`
                    (1440px vs 1280px) and a tighter `lg:px-12` so the brand
                    cluster + 7 nav links + CTA can all sit inside the viewport
                    without the CTA being clipped at xl/2xl widths. */}
                <div className="mx-auto w-full max-w-[1440px] px-6 md:px-10 lg:px-12 flex items-center justify-between gap-6 h-[76px] md:h-[84px]">
                    {/* Gold ACT monogram + wordmark. */}
                    <Link
                        to="/"
                        className="group flex items-center gap-3 flex-shrink-0"
                        aria-label="Advanced Connected Technologies — return to homepage"
                        data-testid="site-logo-home"
                    >
                        <span
                            aria-hidden="true"
                            className="inline-block font-serif text-[22px] text-[#C9A86A] tracking-[0.4em] leading-none transition-colors duration-300 group-hover:text-[#D9BC80]"
                        >
                            ACT
                        </span>
                        <span
                            aria-hidden="true"
                            className="hidden 2xl:inline-block h-4 w-px bg-[rgba(201,168,106,0.35)]"
                        />
                        <span className="hidden 2xl:inline-block text-[10px] font-medium text-[#F3EFE7] tracking-[0.3em] uppercase whitespace-nowrap">
                            Advanced Connected Technologies
                        </span>
                    </Link>

                    {/* Desktop links */}
                    <nav
                        aria-label="Primary"
                        className="hidden lg:flex items-center gap-5 xl:gap-7"
                    >
                        {NAV_LINKS.map((link) => (
                            <NavLink
                                key={link.to}
                                to={link.to}
                                end={link.to === "/"}
                                className={({ isActive }) =>
                                    `text-[11.5px] tracking-[0.22em] uppercase font-medium transition-colors duration-300 ease-out hover:text-[#D9BC80] whitespace-nowrap ${
                                        isActive
                                            ? "text-[#C9A86A]"
                                            : "text-[#F3EFE7]"
                                    }`
                                }
                                data-testid={`nav-link-${link.label.toLowerCase()}`}
                            >
                                {link.label}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="flex items-center gap-4 flex-shrink-0">
                        <GoldButton
                            to="/contact"
                            variant="outline"
                            className="hidden md:inline-flex px-[1.2rem] py-[0.7rem] text-[10px] tracking-[0.2em] whitespace-nowrap"
                            testId="nav-book-consultation"
                        >
                            Book Consultation
                        </GoldButton>

                        <button
                            type="button"
                            onClick={() => setMobileOpen(true)}
                            aria-label="Open menu"
                            className="lg:hidden inline-flex items-center justify-center w-11 h-11 text-[#F3EFE7] hover:text-[#C9A86A] transition-colors duration-300"
                            data-testid="mobile-menu-open"
                        >
                            <Menu size={22} strokeWidth={1.2} />
                        </button>
                    </div>
                </div>
                <div
                    aria-hidden="true"
                    className={`transition-opacity duration-500 ${
                        transparent ? "opacity-0" : "opacity-100"
                    }`}
                >
                    <div className="hairline-solid" />
                </div>
            </header>

            <MobileNav
                open={mobileOpen}
                onClose={() => setMobileOpen(false)}
            />

            {/* Spacer not needed on home (hero is full bleed). On other routes we add top padding at page level. */}
            {!isHome && <div aria-hidden="true" className="h-[76px] md:h-[84px]" />}
            <span className="sr-only" data-testid="brand-phone-sr">
                {BRAND.phone}
            </span>
        </>
    );
}
